import {
  CaptureUpdateAction,
  Drawink,
  TTDDialogTrigger,
  reconcileElements,
  useEditorInterface,
} from "@/core";
import { trackEvent } from "@/core/analytics";
import { getDefaultAppState } from "@/core/appState";
import {
  CommandPalette,
  DEFAULT_CATEGORIES,
} from "@/core/components/CommandPalette/CommandPalette";
import { ErrorDialog } from "@/core/components/ErrorDialog";
import { OverwriteConfirmDialog } from "@/core/components/OverwriteConfirm/OverwriteConfirm";
import { openConfirmModal } from "@/core/components/OverwriteConfirm/OverwriteConfirmState";
import { ShareableLinkDialog } from "@/core/components/ShareableLinkDialog";
import Trans from "@/core/components/Trans";
import DropdownMenuItem from "@/core/components/dropdownMenu/DropdownMenuItem";
import { loadFromBlob } from "@/core/data/blob";
import { useCallbackRefState } from "@/core/hooks/useCallbackRefState";
import { t } from "@/core/i18n";
import polyfill from "@/core/polyfill";
import {
  APP_NAME,
  EVENT,
  THEME,
  VERSION_TIMEOUT,
  debounce,
  getFrame,
  getVersion,
  // isRunningInIframe,
  isDevEnv,
  isTestEnv,
  preventUnload,
  resolvablePromise,
} from "@/lib/common";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  DiscordIcon,
  ExcalLogo,
  GithubIcon,
  XBrandIcon,
  share,
  usersIcon,
  youtubeIcon,
} from "@/core/components/icons";
import { parseLibraryTokensFromUrl, useHandleLibrary } from "@/core/data/library";
import { restore, restoreAppState } from "@/core/data/restore";
import { isElementLink } from "@/lib/elements";
import { newElementWith } from "@/lib/elements";
import { isInitializedImageElement } from "@/lib/elements";
import clsx from "clsx";

import type { RemoteDrawinkElement } from "@/core/data/reconcile";
import type { RestoredDataState } from "@/core/data/restore";
import type {
  AppState,
  BinaryFiles,
  DrawinkImperativeAPI,
  DrawinkInitialDataState,
  UIAppState,
} from "@/core/types";
import type { ResolutionType } from "@/lib/common/utility-types";
import type { ResolvablePromise } from "@/lib/common/utils";
import type { FileId, NonDeletedDrawinkElement, OrderedDrawinkElement } from "@/lib/elements/types";

import CustomStats from "./CustomStats";
import {
  Provider,
  appJotaiStore,
  useAtom,
  useAtomValue,
  useAtomWithInitialValue,
} from "./app-jotai";
import { STORAGE_KEYS, SYNC_BROWSER_TABS_TIMEOUT, isDrawinkPlusSignedUser } from "./app_constants";
import Collab, { collabAPIAtom, isCollaboratingAtom, isOfflineAtom } from "./collab/Collab";
import { AppFooter } from "./components/AppFooter";
import { AppMainMenu } from "./components/AppMainMenu";
import { AppWelcomeScreen } from "./components/AppWelcomeScreen";
import { TopErrorBoundary } from "./components/TopErrorBoundary";

import { exportToBackend, getCollaborationLinkData, isCollaborationLink, loadScene } from "./data";

import { updateStaleImageStatuses } from "./data/FileManager";
import { importFromLocalStorage, importUsernameFromLocalStorage } from "./data/localStorage";

import { DrawinkPlusIframeExport } from "./DrawinkPlusIframeExport";
import { getPreferredLanguage } from "./app-language/language-detector";
import { useAppLangCode } from "./app-language/language-state";
import CollabError, { collabErrorIndicatorAtom } from "./collab/CollabError";
import { AIComponents } from "./components/AI";
import { AuthDialog } from "./components/AuthDialog";
import DebugCanvas, {
  debugRenderer,
  isVisualDebuggerEnabled,
  loadSavedDebugState,
} from "./components/DebugCanvas";
import { hybridStorageAdapter, localStorageQuotaExceededAtom } from "./data/HybridStorageAdapter";
import {
  LibraryIndexedDBAdapter,
  LibraryLocalStorageMigrationAdapter,
} from "./data/LocalStorageAdapter";
import { deriveConvexAccessToken } from "./data/convexAccess";
import { loadFilesFromConvex } from "./data/convexFiles";
import { isBrowserStorageStateNewer } from "./data/tabSync";
import { ShareDialog, shareDialogStateAtom } from "./share/ShareDialog";
import { useHandleAppTheme } from "./useHandleAppTheme";

import "./index.css"; // Tailwind CSS
import "./index.scss"; // Legacy SCSS (to be migrated)

import { type AuthUser, authStateAtom, cloudEnabledAtom } from "@/core/atoms/auth";
import { boardsAPIAtom, currentBoardIdAtom, currentWorkspaceIdAtom } from "@/core/atoms/boards";
import { editorJotaiStore } from "@/core/editor-jotai";
import { useAuth, useClerk, useUser } from "@clerk/clerk-react";
import { AppSidebar } from "./components/AppSidebar";
import { DrawinkPlusPromoBanner } from "./components/DrawinkPlusPromoBanner";
import { useBoardRoute } from "./hooks/useBoardRoute";

import type { CollabAPI } from "./collab/Collab";

polyfill();

// Initialize boards API atom with HybridStorageAdapter
// @ts-ignore - jotai store type inference limitation
editorJotaiStore.set(boardsAPIAtom, hybridStorageAdapter);

window.DRAWINK_THROTTLE_RENDER = true;

declare global {
  interface BeforeInstallPromptEventChoiceResult {
    outcome: "accepted" | "dismissed";
  }

  interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<BeforeInstallPromptEventChoiceResult>;
  }

  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

let pwaEvent: BeforeInstallPromptEvent | null = null;

// Adding a listener outside of the component as it may (?) need to be
// subscribed early to catch the event.
//
// Also note that it will fire only if certain heuristics are met (user has
// used the app for some time, etc.)
window.addEventListener("beforeinstallprompt", (event: BeforeInstallPromptEvent) => {
  // prevent Chrome <= 67 from automatically showing the prompt
  event.preventDefault();
  // cache for later use
  pwaEvent = event;
});

let isSelfEmbedding = false;

if (window.self !== window.top) {
  try {
    const parentUrl = new URL(document.referrer);
    const currentUrl = new URL(window.location.href);
    if (parentUrl.origin === currentUrl.origin) {
      isSelfEmbedding = true;
    }
  } catch (_error) {
    // ignore
  }
}

const shareableLinkConfirmDialog = {
  title: t("overwriteConfirm.modal.shareableLink.title"),
  description: (
    <Trans
      i18nKey="overwriteConfirm.modal.shareableLink.description"
      bold={(text) => <strong>{text}</strong>}
      br={() => <br />}
    />
  ),
  actionLabel: t("overwriteConfirm.modal.shareableLink.button"),
  color: "danger",
} as const;

/**
 * Returns the best URL to restore after loading external content.
 * If the user has a last-opened board, return that board URL;
 * otherwise fall back to origin (root).
 */
const getPostLoadUrl = (): string => {
  const lastBoardId = localStorage.getItem(STORAGE_KEYS.LOCAL_STORAGE_CURRENT_BOARD_ID);
  const lastWsId = localStorage.getItem("selectedWorkspaceId");
  if (lastBoardId && lastWsId) {
    return `/workspace/${lastWsId}/board/${lastBoardId}`;
  }
  return window.location.origin;
};

const initializeScene = async (opts: {
  collabAPI: CollabAPI | null;
  drawinkAPI: DrawinkImperativeAPI;
}): Promise<
  { scene: DrawinkInitialDataState | null } & (
    | {
        isExternalScene: true;
        id: string;
        key: string;
        source: "legacyShare" | "publicShare" | "room";
      }
    | { isExternalScene: false; id?: null; key?: null }
  )
> => {
  const searchParams = new URLSearchParams(window.location.search);
  const id = searchParams.get("id");
  const jsonBackendMatch = window.location.hash.match(/^#json=([a-zA-Z0-9_-]+),([a-zA-Z0-9_-]+)$/);
  const shareMatch = window.location.hash.match(/^#share=([a-zA-Z0-9_-]+),([a-zA-Z0-9_-]+)$/);
  const externalUrlMatch = window.location.hash.match(/^#url=(.*)$/);

  // Backward compat: bare board ID in hash (old /#boardId format)
  // Redirect to proper /workspace/:wsId/board/:boardId URL
  if (window.location.hash && !jsonBackendMatch && !shareMatch && !externalUrlMatch) {
    const bareBoardIdMatch = window.location.hash.match(/^#([a-zA-Z0-9_]+)$/);
    if (bareBoardIdMatch) {
      const boardId = bareBoardIdMatch[1];
      const wsId = localStorage.getItem("selectedWorkspaceId");
      if (wsId) {
        localStorage.setItem(STORAGE_KEYS.LOCAL_STORAGE_CURRENT_BOARD_ID, boardId);
        window.history.replaceState({}, "", `/workspace/${wsId}/board/${boardId}`);
      }
    }
  }

  const localDataState = importFromLocalStorage();

  let scene: RestoredDataState & {
    scrollToContent?: boolean;
  } = await loadScene(null, null, localDataState);

  let roomLinkData = getCollaborationLinkData(window.location.href);
  const isExternalScene = !!(id || jsonBackendMatch || shareMatch || roomLinkData);
  if (isExternalScene) {
    if (
      // don't prompt if scene is empty
      !scene.elements.length ||
      // don't prompt for collab scenes because we don't override local storage
      roomLinkData ||
      // otherwise, prompt whether user wants to override current scene
      (await openConfirmModal(shareableLinkConfirmDialog))
    ) {
      if (jsonBackendMatch) {
        scene = await loadScene(jsonBackendMatch[1], jsonBackendMatch[2], localDataState);
      } else if (shareMatch) {
        // Load from Convex using the proof derived from the fragment key.
        const { importFromConvex } = await import("@/data/index");
        const shareData = await importFromConvex(shareMatch[1], shareMatch[2]);
        scene = restore(shareData, localDataState?.appState, localDataState?.elements, {
          repairBindings: true,
          refreshDimensions: false,
          deleteInvisibleElements: true,
        });
      }
      scene.scrollToContent = true;
      if (!roomLinkData) {
        window.history.replaceState({}, APP_NAME, getPostLoadUrl());
      }
    } else {
      // https://github.com/drawink/drawink/issues/1919
      if (document.hidden) {
        return new Promise((resolve, reject) => {
          window.addEventListener(
            "focus",
            () => initializeScene(opts).then(resolve).catch(reject),
            {
              once: true,
            },
          );
        });
      }

      roomLinkData = null;
      window.history.replaceState({}, APP_NAME, getPostLoadUrl());
    }
  } else if (externalUrlMatch) {
    window.history.replaceState({}, APP_NAME, getPostLoadUrl());

    const url = externalUrlMatch[1];
    try {
      const request = await fetch(window.decodeURIComponent(url));
      const data = await loadFromBlob(await request.blob(), null, null);
      if (!scene.elements.length || (await openConfirmModal(shareableLinkConfirmDialog))) {
        // @ts-ignore - complex discriminated union type
        return { scene: data, isExternalScene };
      }
    } catch (_error: any) {
      // @ts-ignore - complex discriminated union type
      return {
        scene: {
          appState: {
            errorMessage: t("alerts.invalidSceneUrl"),
          },
        },
        isExternalScene: isExternalScene as false,
      };
    }
  }

  if (roomLinkData && opts.collabAPI) {
    const { drawinkAPI } = opts;

    const scene = await opts.collabAPI.startCollaboration(roomLinkData);

    return {
      // when collaborating, the state may have already been updated at this
      // point (we may have received updates from other clients), so reconcile
      // elements and appState with existing state
      scene: {
        ...scene,
        appState: {
          ...restoreAppState(
            {
              ...scene?.appState,
              theme: localDataState?.appState?.theme || scene?.appState?.theme,
            },
            drawinkAPI.getAppState(),
          ),
          // necessary if we're invoking from a hashchange handler which doesn't
          // go through App.initializeScene() that resets this flag
          isLoading: false,
        },
        elements: reconcileElements(
          scene?.elements || [],
          drawinkAPI.getSceneElementsIncludingDeleted() as RemoteDrawinkElement[],
          drawinkAPI.getAppState(),
        ),
      },
      isExternalScene: true,
      id: roomLinkData.roomId,
      key: roomLinkData.roomKey,
      source: "room",
    };
  }
  if (scene) {
    return isExternalScene && (jsonBackendMatch || shareMatch)
      ? {
          scene,
          isExternalScene,
          id: (jsonBackendMatch ?? shareMatch)![1],
          key: (jsonBackendMatch ?? shareMatch)![2],
          source: jsonBackendMatch ? "legacyShare" : "publicShare",
        }
      : { scene, isExternalScene: false };
  }
  return { scene: null, isExternalScene: false };
};

const DrawinkWrapper = () => {
  const [errorMessage, setErrorMessage] = useState("");
  const isCollabDisabled = false;

  const { editorTheme, appTheme, setAppTheme } = useHandleAppTheme();

  const [langCode, setLangCode] = useAppLangCode();

  const _editorInterface = useEditorInterface();

  // Board route params (/workspace/:wsId/board/:boardId)
  const boardRoute = useBoardRoute();

  // Set board from URL route params BEFORE initializeScene runs.
  // importFromLocalStorage() reads "drawink-current-board-id" from localStorage
  // to decide which board data to load, so we set it synchronously here.
  useEffect(() => {
    if (!boardRoute) {
      return;
    }
    const { workspaceId, boardId } = boardRoute;

    // Synchronously set current board ID so importFromLocalStorage reads the right data
    localStorage.setItem(STORAGE_KEYS.LOCAL_STORAGE_CURRENT_BOARD_ID, boardId);
    localStorage.setItem("selectedWorkspaceId", workspaceId);

    // Set workspace on cloud adapter (async, but localStorage is already set)
    hybridStorageAdapter.setWorkspaceAndBoard(workspaceId, boardId).catch(console.error);

    // Update Jotai atoms
    editorJotaiStore.set(currentWorkspaceIdAtom, workspaceId);
    editorJotaiStore.set(currentBoardIdAtom, boardId);
  }, [boardRoute]);

  // initial state
  // ---------------------------------------------------------------------------

  const initialStatePromiseRef = useRef<{
    promise: ResolvablePromise<DrawinkInitialDataState | null>;
  }>({ promise: null! });
  if (!initialStatePromiseRef.current.promise) {
    initialStatePromiseRef.current.promise = resolvablePromise<DrawinkInitialDataState | null>();
  }

  const debugCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    trackEvent("load", "frame", getFrame());
    // Delayed so that the app has a time to load the latest SW
    setTimeout(() => {
      trackEvent("load", "version", getVersion());
    }, VERSION_TIMEOUT);
  }, []);

  const [drawinkAPI, drawinkRefCallback] = useCallbackRefState<DrawinkImperativeAPI>();

  const [, setShareDialogState] = useAtom(shareDialogStateAtom);
  const [collabAPI] = useAtom(collabAPIAtom);
  const [isCollaborating] = useAtomWithInitialValue(isCollaboratingAtom, () => {
    return isCollaborationLink(window.location.href);
  });
  const collabError = useAtomValue(collabErrorIndicatorAtom);

  useHandleLibrary({
    drawinkAPI,
    adapter: LibraryIndexedDBAdapter,
    // TODO maybe remove this in several months (shipped: 24-03-11)
    migrationAdapter: LibraryLocalStorageMigrationAdapter,
  });

  const [, forceRefresh] = useState(false);

  useEffect(() => {
    if (isDevEnv()) {
      const debugState = loadSavedDebugState();

      if (debugState.enabled && !window.visualDebug) {
        window.visualDebug = {
          data: [],
        };
      } else {
        delete window.visualDebug;
      }
      forceRefresh((prev) => !prev);
    }
  }, [drawinkAPI]);

  // Clerk auth state
  const { user: clerkUser, isLoaded: isUserLoaded, isSignedIn } = useUser();
  const { signOut } = useClerk();
  const { getToken } = useAuth();

  // Clerk auth state listener
  useEffect(() => {
    if (!isUserLoaded) {
      // Still loading auth state
      editorJotaiStore.set(authStateAtom, {
        isAuthenticated: false,
        user: null,
        isLoading: true,
        error: null,
      });
      return;
    }

    if (clerkUser) {
      // User is signed in
      const authUser: AuthUser = {
        uid: clerkUser.id,
        email: clerkUser.primaryEmailAddress?.emailAddress || null,
        displayName: clerkUser.fullName || clerkUser.username || null,
        photoURL: clerkUser.imageUrl || null,
        providerId: clerkUser.externalAccounts[0]?.provider || "clerk",
        username: clerkUser.username || null,
        firstName: clerkUser.firstName || null,
        lastName: clerkUser.lastName || null,
      };

      editorJotaiStore.set(authStateAtom, {
        isAuthenticated: true,
        user: authUser,
        isLoading: false,
        error: null,
      });

      // Enable cloud sync with authentication
      // Wrap getToken to always use the "convex" JWT template
      const fetchConvexToken = async () => {
        try {
          return await getToken({ template: "convex" });
        } catch (error) {
          console.error("[Auth] Failed to get Convex token:", error);
          return null;
        }
      };
      hybridStorageAdapter.enableCloudSync(clerkUser.id, fetchConvexToken);
      editorJotaiStore.set(cloudEnabledAtom, true);

      console.log("[Auth] User signed in:", authUser.email);

      // Ensure workspace exists after a short delay (give sync engine time to try first)
      setTimeout(() => {
        // Check if sync is in error state or if we should ensure workspace
        const currentStatus = hybridStorageAdapter.getSyncStatus();
        if (!currentStatus.isOnline || currentStatus.pendingOperations > 0) {
          console.log("[Auth] Sync status indicates issues - ensuring workspace...");
          hybridStorageAdapter.ensureWorkspaceAndSync().catch(console.error);
        }
      }, 3000); // Wait 3 seconds for initial sync attempt

      // Expose helper for manual workspace creation (dev/debugging)
      if (isDevEnv()) {
        (window as any).__DRAWINK_ensureWorkspace = () => {
          console.log("[Debug] Manually triggering workspace creation...");
          return hybridStorageAdapter.ensureWorkspaceAndSync();
        };
      }
    } else {
      // User is signed out
      editorJotaiStore.set(authStateAtom, {
        isAuthenticated: false,
        user: null,
        isLoading: false,
        error: null,
      });

      // Disable cloud sync
      hybridStorageAdapter.disableCloudSync();
      editorJotaiStore.set(cloudEnabledAtom, false);

      console.log("[Auth] User signed out");
    }
  }, [clerkUser, isUserLoaded]);

  useEffect(() => {
    if (!drawinkAPI || (!isCollabDisabled && !collabAPI)) {
      return;
    }

    const loadImages = (data: ResolutionType<typeof initializeScene>, isInitialLoad = false) => {
      if (!data.scene) {
        return;
      }
      if (collabAPI?.isCollaborating()) {
        if (data.scene.elements) {
          collabAPI
            .fetchImageFilesFromConvex({
              elements: data.scene.elements,
              forceFetchFiles: true,
            })
            .then(({ loadedFiles, erroredFiles }) => {
              drawinkAPI.addFiles(loadedFiles);
              updateStaleImageStatuses({
                drawinkAPI,
                erroredFiles,
                elements: drawinkAPI.getSceneElementsIncludingDeleted(),
              });
            });
        }
      } else {
        const fileIds =
          data.scene.elements?.reduce((acc, element) => {
            if (isInitializedImageElement(element)) {
              return acc.concat(element.fileId);
            }
            return acc;
          }, [] as FileId[]) || [];

        if (data.isExternalScene && data.source !== "room") {
          deriveConvexAccessToken(data.source, data.key)
            .then((accessToken) =>
              loadFilesFromConvex({
                scope: data.source,
                scopeId: data.id,
                accessToken,
                decryptionKey: data.key,
                fileIds,
              }),
            )
            .then(({ loadedFiles, erroredFiles }) => {
              drawinkAPI.addFiles(loadedFiles);
              updateStaleImageStatuses({
                drawinkAPI,
                erroredFiles,
                elements: drawinkAPI.getSceneElementsIncludingDeleted(),
              });
            })
            .catch((error) => console.error("Failed to load shared files from Convex", error));
        } else if (isInitialLoad) {
          if (fileIds.length) {
            hybridStorageAdapter.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  drawinkAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  drawinkAPI,
                  erroredFiles,
                  elements: drawinkAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
          // on fresh load, clear unused files from IDB (from previous
          // session)
          hybridStorageAdapter.fileStorage.clearObsoleteFiles({ currentFileIds: fileIds });
        }
      }
    };

    initializeScene({ collabAPI, drawinkAPI }).then(async (data) => {
      loadImages(data, /* isInitialLoad */ true);
      initialStatePromiseRef.current.promise.resolve(data.scene);
    });

    const onHashChange = async (event: HashChangeEvent) => {
      event.preventDefault();
      const libraryUrlTokens = parseLibraryTokensFromUrl();
      if (!libraryUrlTokens) {
        if (collabAPI?.isCollaborating() && !isCollaborationLink(window.location.href)) {
          collabAPI.stopCollaboration(false);
        }
        drawinkAPI.updateScene({ appState: { isLoading: true } });

        initializeScene({ collabAPI, drawinkAPI }).then((data) => {
          loadImages(data);
          if (data.scene) {
            drawinkAPI.updateScene({
              ...data.scene,
              ...restore(data.scene, null, null, { repairBindings: true }),
              captureUpdate: CaptureUpdateAction.IMMEDIATELY,
            });
          }
        });
      }
    };

    const syncData = debounce(() => {
      if (isTestEnv()) {
        return;
      }
      if (!document.hidden && ((collabAPI && !collabAPI.isCollaborating()) || isCollabDisabled)) {
        // don't sync if local state is newer or identical to browser state
        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_DATA_STATE)) {
          const localDataState = importFromLocalStorage();
          const username = importUsernameFromLocalStorage();
          setLangCode(getPreferredLanguage());
          drawinkAPI.updateScene({
            ...localDataState,
            captureUpdate: CaptureUpdateAction.NEVER,
          });
          LibraryIndexedDBAdapter.load().then((data) => {
            if (data) {
              drawinkAPI.updateLibrary({
                libraryItems: data.libraryItems,
              });
            }
          });
          collabAPI?.setUsername(username || "");
        }

        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_FILES)) {
          const elements = drawinkAPI.getSceneElementsIncludingDeleted();
          const currFiles = drawinkAPI.getFiles();
          const fileIds =
            elements?.reduce((acc, element) => {
              if (
                isInitializedImageElement(element) &&
                // only load and update images that aren't already loaded
                !currFiles[element.fileId]
              ) {
                return acc.concat(element.fileId);
              }
              return acc;
            }, [] as FileId[]) || [];
          if (fileIds.length) {
            hybridStorageAdapter.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  drawinkAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  drawinkAPI,
                  erroredFiles,
                  elements: drawinkAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
        }
      }
    }, SYNC_BROWSER_TABS_TIMEOUT);

    const onUnload = () => {
      hybridStorageAdapter.flushSave();
    };

    const visibilityChange = (event: FocusEvent | Event) => {
      if (event.type === EVENT.BLUR || document.hidden) {
        hybridStorageAdapter.flushSave();
      }
      if (event.type === EVENT.VISIBILITY_CHANGE || event.type === EVENT.FOCUS) {
        syncData();
      }
    };

    window.addEventListener(EVENT.HASHCHANGE, onHashChange, false);
    window.addEventListener(EVENT.UNLOAD, onUnload, false);
    window.addEventListener(EVENT.BLUR, visibilityChange, false);
    document.addEventListener(EVENT.VISIBILITY_CHANGE, visibilityChange, false);
    window.addEventListener(EVENT.FOCUS, visibilityChange, false);
    return () => {
      window.removeEventListener(EVENT.HASHCHANGE, onHashChange, false);
      window.removeEventListener(EVENT.UNLOAD, onUnload, false);
      window.removeEventListener(EVENT.BLUR, visibilityChange, false);
      window.removeEventListener(EVENT.FOCUS, visibilityChange, false);
      document.removeEventListener(EVENT.VISIBILITY_CHANGE, visibilityChange, false);
    };
  }, [isCollabDisabled, collabAPI, drawinkAPI, setLangCode]);

  useEffect(() => {
    const unloadHandler = (event: BeforeUnloadEvent) => {
      hybridStorageAdapter.flushSave();

      if (
        drawinkAPI &&
        hybridStorageAdapter.fileStorage.shouldPreventUnload(drawinkAPI.getSceneElements())
      ) {
        if (import.meta.env.VITE_APP_DISABLE_PREVENT_UNLOAD !== "true") {
          preventUnload(event);
        } else {
          console.warn("preventing unload disabled (VITE_APP_DISABLE_PREVENT_UNLOAD)");
        }
      }
    };
    window.addEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    return () => {
      window.removeEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    };
  }, [drawinkAPI]);

  // Handle board switching without page reload
  useEffect(() => {
    if (!drawinkAPI) {
      return;
    }

    const handleBoardSwitch = (event: Event) => {
      const customEvent = event as CustomEvent<{ boardId: string }>;
      const { boardId } = customEvent.detail;

      // Flush current board's data before loading new one
      hybridStorageAdapter.flushSave();

      // Load new board's data
      const { elements, appState } = hybridStorageAdapter.loadBoardData(boardId);

      // Update scene with new board's data
      drawinkAPI.updateScene({
        elements: elements || [],
        appState: {
          ...appState,
          isLoading: false,
        },
        captureUpdate: CaptureUpdateAction.IMMEDIATELY,
      });

      // Load images for the new board
      const fileIds = (elements || [])
        .filter((el) => isInitializedImageElement(el))
        .map((el) => (el as any).fileId);

      if (fileIds.length > 0) {
        hybridStorageAdapter.fileStorage.getFiles(fileIds).then(({ loadedFiles, erroredFiles }) => {
          if (loadedFiles.length) {
            drawinkAPI.addFiles(loadedFiles);
          }
          updateStaleImageStatuses({
            drawinkAPI,
            erroredFiles,
            elements: drawinkAPI.getSceneElementsIncludingDeleted(),
          });
        });
      }

      // Sync URL to reflect the new board
      const wsId = localStorage.getItem("selectedWorkspaceId");
      if (wsId) {
        window.history.replaceState({}, "", `/workspace/${wsId}/board/${boardId}`);
      }
    };

    window.addEventListener("drawink-board-switch", handleBoardSwitch);
    return () => {
      window.removeEventListener("drawink-board-switch", handleBoardSwitch);
    };
  }, [drawinkAPI]);

  const onChange = (
    elements: readonly OrderedDrawinkElement[],
    appState: AppState,
    files: BinaryFiles,
  ) => {
    if (collabAPI?.isCollaborating()) {
      collabAPI.syncElements(elements);
    }

    // this check is redundant, but since this is a hot path, it's best
    // not to evaludate the nested expression every time
    if (!hybridStorageAdapter.isSavePaused()) {
      hybridStorageAdapter.save(elements, appState, files, () => {
        if (drawinkAPI) {
          let didChange = false;

          const elements = drawinkAPI.getSceneElementsIncludingDeleted().map((element) => {
            if (hybridStorageAdapter.fileStorage.shouldUpdateImageElementStatus(element)) {
              const newElement = newElementWith(element, { status: "saved" });
              if (newElement !== element) {
                didChange = true;
              }
              return newElement;
            }
            return element;
          });

          if (didChange) {
            drawinkAPI.updateScene({
              elements,
              captureUpdate: CaptureUpdateAction.NEVER,
            });
          }
        }
      });
    }

    // Render the debug scene if the debug canvas is available
    if (debugCanvasRef.current && drawinkAPI) {
      debugRenderer(debugCanvasRef.current, appState, elements, window.devicePixelRatio);
    }
  };

  const [latestShareableLink, setLatestShareableLink] = useState<string | null>(null);

  const onExportToBackend = async (
    exportedElements: readonly NonDeletedDrawinkElement[],
    appState: Partial<AppState>,
    files: BinaryFiles,
  ) => {
    if (exportedElements.length === 0) {
      throw new Error(t("alerts.cannotExportEmptyCanvas"));
    }
    try {
      const { url, errorMessage } = await exportToBackend(
        exportedElements,
        {
          ...appState,
          viewBackgroundColor: appState.exportBackground
            ? appState.viewBackgroundColor
            : getDefaultAppState().viewBackgroundColor,
        },
        files,
      );

      if (errorMessage) {
        throw new Error(errorMessage);
      }

      if (url) {
        setLatestShareableLink(url);
      }
    } catch (error: any) {
      if (error.name !== "AbortError") {
        const { width, height } = appState;
        console.error(error, {
          width,
          height,
          devicePixelRatio: window.devicePixelRatio,
        });
        throw new Error(error.message);
      }
    }
  };

  const renderCustomStats = (
    elements: readonly NonDeletedDrawinkElement[],
    appState: UIAppState,
  ) => {
    return (
      <CustomStats
        setToast={(message) => drawinkAPI!.setToast({ message })}
        appState={appState}
        elements={elements}
      />
    );
  };

  const isOffline = useAtomValue(isOfflineAtom);

  const localStorageQuotaExceeded = useAtomValue(localStorageQuotaExceededAtom);

  const onCollabDialogOpen = useCallback(
    () => setShareDialogState({ isOpen: true, type: "collaborationOnly" }),
    [setShareDialogState],
  );

  // browsers generally prevent infinite self-embedding, there are
  // cases where it still happens, and while we disallow self-embedding
  // by not whitelisting our own origin, this serves as an additional guard
  if (isSelfEmbedding) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          height: "100%",
        }}
      >
        <h1>I'm not a pretzel!</h1>
      </div>
    );
  }

  const DrawinkPlusCommand = {
    label: "Drawink Pro",
    category: DEFAULT_CATEGORIES.links,
    predicate: true,
    icon: <div style={{ width: 14 }}>{ExcalLogo}</div>,
    keywords: ["plus", "cloud", "server"],
    perform: () => {
      window.open("/plus?utm_source=drawink&utm_medium=app&utm_content=command_palette", "_blank");
    },
  };
  const DrawinkPlusAppCommand = {
    label: "Sign up",
    category: DEFAULT_CATEGORIES.links,
    predicate: true,
    icon: <div style={{ width: 14 }}>{ExcalLogo}</div>,
    keywords: ["drawink", "plus", "cloud", "server", "signin", "login", "signup"],
    perform: () => {
      window.open(
        `${
          import.meta.env.VITE_APP_PLUS_APP
        }?utm_source=drawink&utm_medium=app&utm_content=command_palette`,
        "_blank",
      );
    },
  };

  return (
    <div
      style={{ height: "100%" }}
      className={clsx("drawink-app", {
        "is-collaborating": isCollaborating,
      })}
    >
      <Drawink
        drawinkAPI={drawinkRefCallback}
        onChange={onChange}
        initialData={initialStatePromiseRef.current.promise}
        isCollaborating={isCollaborating}
        onPointerUpdate={collabAPI?.onPointerUpdate}
        UIOptions={{
          canvasActions: {
            toggleTheme: true,
            export: {
              onExportToBackend,
            },
          },
        }}
        langCode={langCode}
        renderCustomStats={renderCustomStats}
        detectScroll={false}
        handleKeyboardGlobally={true}
        autoFocus={true}
        theme={editorTheme}
        renderTopRightUI={(_isMobile) => {
          return (
            <div className="drawink-ui-top-right">
              {isSignedIn && (
                <button
                  type="button"
                  className="dashboard-btn"
                  onClick={() => {
                    window.location.href = "/dashboard";
                  }}
                  title="Go to Dashboard"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "36px",
                    height: "36px",
                    border: "none",
                    borderRadius: "8px",
                    background: "var(--color-surface-mid)",
                    cursor: "pointer",
                    color: "var(--color-on-surface)",
                  }}
                >
                  <svg
                    aria-hidden="true"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="3" width="7" height="7" rx="1" />
                    <rect x="14" y="3" width="7" height="7" rx="1" />
                    <rect x="3" y="14" width="7" height="7" rx="1" />
                    <rect x="14" y="14" width="7" height="7" rx="1" />
                  </svg>
                </button>
              )}
              {drawinkAPI?.getEditorInterface().formFactor === "desktop" && (
                <DrawinkPlusPromoBanner isSignedIn={isDrawinkPlusSignedUser} />
              )}

              {collabError.message && <CollabError collabError={collabError} />}

              {/* Organization Selector */}
              {/* TODO: Migrate to Convex or start tRPC backend */}
              {/* {!isMobile && <OrganizationSelector />} */}

              <DropdownMenuItem
                data-testid="share-button"
                className="share-button"
                onSelect={() => setShareDialogState({ isOpen: true, type: "share" })}
                icon={share}
              >
                {null}
              </DropdownMenuItem>
            </div>
          );
        }}
        onLinkOpen={(element, event) => {
          if (element.link && isElementLink(element.link)) {
            event.preventDefault();
            drawinkAPI?.scrollToContent(element.link, { animate: true });
          }
        }}
      >
        <AppMainMenu
          onCollabDialogOpen={onCollabDialogOpen}
          isCollaborating={isCollaborating}
          isCollabEnabled={!isCollabDisabled}
          theme={appTheme}
          setTheme={(theme) => setAppTheme(theme)}
          refresh={() => forceRefresh((prev) => !prev)}
        />
        <AppWelcomeScreen
          onCollabDialogOpen={onCollabDialogOpen}
          isCollabEnabled={!isCollabDisabled}
        />
        <OverwriteConfirmDialog>
          <OverwriteConfirmDialog.Actions.ExportToImage />
          <OverwriteConfirmDialog.Actions.SaveToDisk />
        </OverwriteConfirmDialog>
        <AppFooter onChange={() => drawinkAPI?.refresh()} />
        {drawinkAPI && <AIComponents drawinkAPI={drawinkAPI} />}

        <TTDDialogTrigger />
        {isCollaborating && isOffline && (
          <div className="alert alert--warning">{t("alerts.collabOfflineWarning")}</div>
        )}
        {localStorageQuotaExceeded && (
          <div className="alert alert--danger">{t("alerts.localStorageQuotaExceeded")}</div>
        )}
        {latestShareableLink && (
          <ShareableLinkDialog
            link={latestShareableLink}
            onCloseRequest={() => setLatestShareableLink(null)}
            setErrorMessage={setErrorMessage}
          />
        )}
        {drawinkAPI && !isCollabDisabled && <Collab drawinkAPI={drawinkAPI} />}

        <ShareDialog
          collabAPI={collabAPI}
          onExportToBackend={async () => {
            if (drawinkAPI) {
              try {
                await onExportToBackend(
                  drawinkAPI.getSceneElements(),
                  drawinkAPI.getAppState(),
                  drawinkAPI.getFiles(),
                );
              } catch (error: any) {
                setErrorMessage(error.message);
              }
            }
          }}
        />

        <AuthDialog />

        <AppSidebar />

        {errorMessage && (
          <ErrorDialog onClose={() => setErrorMessage("")}>{errorMessage}</ErrorDialog>
        )}

        <CommandPalette
          customCommandPaletteItems={[
            {
              label: t("labels.liveCollaboration"),
              category: DEFAULT_CATEGORIES.app,
              keywords: ["team", "multiplayer", "share", "public", "session", "invite"],
              icon: usersIcon,
              perform: () => {
                setShareDialogState({
                  isOpen: true,
                  type: "collaborationOnly",
                });
              },
            },
            {
              label: t("roomDialog.button_stopSession"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => !!collabAPI?.isCollaborating(),
              keywords: ["stop", "session", "end", "leave", "close", "exit", "collaboration"],
              perform: () => {
                if (collabAPI) {
                  collabAPI.stopCollaboration();
                  if (!collabAPI.isCollaborating()) {
                    setShareDialogState({ isOpen: false });
                  }
                }
              },
            },
            {
              label: t("labels.share"),
              category: DEFAULT_CATEGORIES.app,
              predicate: true,
              icon: share,
              keywords: [
                "link",
                "shareable",
                "readonly",
                "export",
                "publish",
                "snapshot",
                "url",
                "collaborate",
                "invite",
              ],
              perform: async () => {
                setShareDialogState({ isOpen: true, type: "share" });
              },
            },
            {
              label: "GitHub",
              icon: GithubIcon,
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              keywords: ["issues", "bugs", "requests", "report", "features", "social", "community"],
              perform: () => {
                window.open("https://github.com/drawink/drawink", "_blank", "noopener noreferrer");
              },
            },
            {
              label: t("labels.followUs"),
              icon: XBrandIcon,
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              keywords: ["twitter", "contact", "social", "community"],
              perform: () => {
                window.open("https://x.com/drawink", "_blank", "noopener noreferrer");
              },
            },
            {
              label: t("labels.discordChat"),
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              icon: DiscordIcon,
              keywords: [
                "chat",
                "talk",
                "contact",
                "bugs",
                "requests",
                "report",
                "feedback",
                "suggestions",
                "social",
                "community",
              ],
              perform: () => {
                window.open("https://discord.gg/UexuTaE", "_blank", "noopener noreferrer");
              },
            },
            {
              label: "YouTube",
              icon: youtubeIcon,
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              keywords: ["features", "tutorials", "howto", "help", "community"],
              perform: () => {
                window.open("https://youtube.com/@drawink", "_blank", "noopener noreferrer");
              },
            },
            ...(isDrawinkPlusSignedUser
              ? [
                  {
                    ...DrawinkPlusAppCommand,
                    label: "Sign in / Go to Drawink Pro",
                  },
                ]
              : [DrawinkPlusCommand, DrawinkPlusAppCommand]),

            {
              ...CommandPalette.defaultItems.toggleTheme,
              perform: () => {
                setAppTheme(editorTheme === THEME.DARK ? THEME.LIGHT : THEME.DARK);
              },
            },
            {
              label: t("labels.installPWA"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => !!pwaEvent,
              perform: () => {
                if (pwaEvent) {
                  pwaEvent.prompt();
                  pwaEvent.userChoice.then(() => {
                    // event cannot be reused, but we'll hopefully
                    // grab new one as the event should be fired again
                    pwaEvent = null;
                  });
                }
              },
            },
          ]}
        />
        {isVisualDebuggerEnabled() && drawinkAPI && (
          <DebugCanvas
            appState={drawinkAPI.getAppState()}
            scale={window.devicePixelRatio}
            ref={debugCanvasRef}
          />
        )}
      </Drawink>
    </div>
  );
};

const DrawinkApp = () => {
  const isCloudExportWindow = window.location.pathname === "/drawink-plus-export";
  if (isCloudExportWindow) {
    return <DrawinkPlusIframeExport />;
  }

  return (
    <TopErrorBoundary>
      <Provider store={appJotaiStore}>
        <DrawinkWrapper />
      </Provider>
    </TopErrorBoundary>
  );
};

export default DrawinkApp;
